import React, { useEffect, useState } from 'react';
import { LayoutGrid, LayoutGridItem } from '../../../../../layout/LayoutGrid';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { GridColumns } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { placementDocumentsActions } from '../../../../../../store/redux-store/placement-documents/slice';
import { usePromiseTracker } from 'react-promise-tracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getDetailsPageSupportComponentHeight } from 'app/layout/pages/DetailsPage';
import { useTranslation } from 'react-i18next';
import { deriveColumns } from './utils';
import { useDecision } from '@optimizely/react-sdk';
import {
  ff_placementStatusChangeKey,
  ff_placement_new_client_confirmation_ui_flow,
  ff_placement_ui_documents,
  ff_placement_ui_documents_tab_contract_button,
  ff_placement_wage_request_form_create_external_task,
  ff_placement_ui_displaycredentialingbutton,
  ff_placement_ui_packet_generation,
} from 'app/constants/FeatureFlags/Placement/Keys';
import { selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { selectPlacementDocuments } from 'store/redux-store/placement-documents/selector';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { CreateClientConfirmationXGridBtn } from '@AMIEWEB/Placement/CreateClientConfirmationDoc/CreateClientConfirmationBtn';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import {
  isAssignmentComplete,
  isBooked,
  isBookingApproved,
  isFC,
  isGoodToGo,
  isOnAssignment,
  isPostponementWarning,
  isTA,
  isWaitingGoodToGo,
  placementStatusOptions,
} from '@AMIEWEB/Placement/NextPlacementStatus/StatusDefaults';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { CreateCandidateContractXGridBtn } from '@AMIEWEB/Placement/CreateCandidateContractDoc/CreateCandidateContractBtn';
import { redirectToCandidateContractDocEditor } from '@AMIEWEB/Placement/CreateCandidateContractDoc/utils';
import { DeliveryStatusWrapper } from './DocumentsTabDeliveryStatusWrapper';
import { CredentialingPacketDialog } from '@AMIEWEB/Placement/NextPlacementStatus/Packets/CredentialingPacketDialog';
import { redirectToClientConfirmationDocEditor } from '@AMIEWEB/Placement/CreateClientConfirmationDoc/utils';
import moment from 'moment';
import { DispatchArbitrationAgreementBtn } from './DispatchArbitrationAgreement';
import { ArbitrationAgreementConfirmationModal } from './ArbitrationAgreementConfirmationModal';
import { DispatchWageRequestBtn } from './DispatchWageRequestBtn';
import { ConfirmationModal } from './ConfirmationModal';
import { DispatchCredentialingButton } from './DispatchCredentialingButton';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { useReadOnly } from 'oidc/userRoles';

export enum DocumentButtonType {
  Create_Wage_Request = 'Create_Wage_Request',
}

const getCredentialingPacketGeneratorUrl = (candidateId: number, placementId: number): string => {
  const packetgenUrl = globalThis?.app?.env?.REACT_APP_PACKETGEN_URL;
  if (!packetgenUrl || packetgenUrl === '') {
    return `/placement/${placementId}?tab=0`;
  }
  return `${packetgenUrl}?candidateId=${candidateId}&placementId=${placementId}&r=/placement/${placementId}?tab=4&productId=credpkt`;
};

export const DocumentsTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openEmailDeliveryModal, setEmailDeliveryModal] = useState(false);

  const handleOnClick = (id, emailLogs) => {
    dispatch(placementDocumentsActions.setLogId(id));
    dispatch(placementDocumentsActions.setEmailLogs(emailLogs));
    setEmailDeliveryModal(true);
  };
  const [credModalProps, setCredModalProps] = useState<{
    openCred: boolean;
    documentId: string;
  } | null>(null);
  const [columns, setColumns] = useState<GridColumns>(deriveColumns(t, handleOnClick, setCredModalProps));
  const { documents, logId, emailLogs } = useSelector(selectPlacementDocuments);
  const {
    placementId,
    candidate: { id: candidateId },
  } = useSelector(selectPlacementHeaderData);
  const { readOnly } = useReadOnly();

  const { currentPlacementStatus } = useSelector(selectPlacementStatus);

  const { promiseInProgress } = usePromiseTracker({ area: 'get-placement-documents', delay: 0 });
  const { promiseInProgress: progressBarInProgress } = usePromiseTracker({ area: 'placement-progress-call', delay: 0 });
  const params = useParams<{ placementId: string }>();
  const [enableDocuments] = useDecision(ff_placement_ui_documents);
  const [decision] = useDecision(ff_placementStatusChangeKey, { autoUpdate: true });
  const [ccEditorViewDecision] = useDecision(ff_placement_new_client_confirmation_ui_flow);
  const [wageRequestForm] = useDecision(ff_placement_wage_request_form_create_external_task);
  const [showCredentialingButton] = useDecision(ff_placement_ui_displaycredentialingbutton);
  const [enablePacketGeneration] = useDecision(ff_placement_ui_packet_generation);
  const [contractBtnDecision] = useDecision(ff_placement_ui_documents_tab_contract_button);
  const [confirmArbitrationAgreementCreation, setConfirmArbitrationAgreementCreation] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({ type: '', open: false });
  const [sortModel, setSortModel] = useState<any>([
    {
      field: 'lastUpdated',
      sort: 'desc',
    },
  ]);

  const history = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (enableDocuments?.enabled) {
      dispatch(placementDocumentsActions.getPlacementDocuments({ placementId: parseInt(params.placementId) }));
      if (wageRequestForm?.enabled) {
        dispatch(
          placementDetailsAction.wageRequestFormSignedStatus({
            candidateId: candidateId,
            placementId: parseInt(params.placementId),
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.placementId, enableDocuments?.enabled]);
  const [gridHeight, setGridHeight] = React.useState<number | undefined>(window.innerHeight / 1.9);

  const resizeGrid = React.useCallback(event => {
    updateGridHeight();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      updateGridHeight();
    }, 250);
    window.addEventListener('resize', resizeGrid, true);

    return () => {
      window.removeEventListener('resize', resizeGrid);
    };
  }, [documents, resizeGrid, progressBarInProgress]);

  const updateGridHeight = () => {
    const date = moment();
    const timer = setInterval(() => {
      // Updates the height every 100 milliseconds, for 1.5s while any animation is occurring
      const c = document.getElementById('details-page-paper')?.offsetHeight;
      const d = getDetailsPageSupportComponentHeight();
      if (moment().subtract(1500, 'milliseconds').isBefore(date)) {
        if (c && d) {
          // -12 to account for grid padding
          setGridHeight(c - d - (document.getElementById('xgrid-title-container')?.offsetHeight ?? 0) - 12);
        }
      } else {
        // -12 to account for grid padding
        setGridHeight(c - d - (document.getElementById('xgrid-title-container')?.offsetHeight ?? 0) - 12);
        clearInterval(timer);
      }
    }, 100);
  };
  const handleCreateCCBtnClick = React.useCallback(
    () =>
      ccEditorViewDecision?.enabled
        ? redirectToClientConfirmationDocEditor(history, location, { defaultSelection: 'draft' })
        : dispatch(placementStatusAction.setSelectedStatus({ status: placementStatusOptions.clientConfirmation })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const disableCreateDocBtn = React.useMemo(
    () =>
      !(
        isTA(currentPlacementStatus) ||
        isFC(currentPlacementStatus) ||
        isBooked(currentPlacementStatus) ||
        isBookingApproved(currentPlacementStatus) ||
        isWaitingGoodToGo(currentPlacementStatus) ||
        isGoodToGo(currentPlacementStatus) ||
        isPostponementWarning(currentPlacementStatus) ||
        isOnAssignment(currentPlacementStatus) ||
        isAssignmentComplete(currentPlacementStatus)
      ),
    [currentPlacementStatus],
  );

  const handleSubmitPacketDialogOpen = React.useCallback(() => {
    dispatch(placementStatusAction.setSelectedStatus({ status: placementStatusOptions.submitPacket, statusChangeSource: 'statusDropDown' }));
  }, [dispatch]);

  // const toolbarProps = React.useMemo(
  //   () => ({
  //     leftActions: [
  //       ...CreatePacketXGridBtn({ linkProps: { candidateId, placementId } }),
  //       ...CreateClientConfirmationXGridBtn({
  //         hidden: hideCreateDocBtn,
  //         onClick: openClientConfirmationDialog,
  //       }),
  //       ...CreateCandidateContractXGridBtn({
  //         hidden: hideCreateDocBtn,
  //         onClick: () => redirectToCandidateContractDocEditor(history),
  //       }),
  //     ],
  //   }),
  //   [candidateId, hideCreateDocBtn, history, openClientConfirmationDialog, placementId],
  // );

  // const grid = React.useMemo(
  //   () => (
  //     <XGrid
  //       columns={columns}
  //       rows={documents || []}
  //       loading={promiseInProgress}
  //       toolbarProps={toolbarProps}
  //       sortModel={sortModel}
  //       title={t('placement.profile.tabs.documentTab.title')}
  //       height={gridHeight}
  //       disableSelectionOnClick
  //       onSortModelChange={model => setSortModel(model)}
  //     />
  //   ),
  //   [columns, documents, gridHeight, promiseInProgress, sortModel, t, toolbarProps],
  // );

  return (
    <div>
      <DeliveryStatusWrapper
        openEmailDeliveryModal={openEmailDeliveryModal}
        setEmailDeliveryModal={setEmailDeliveryModal}
        logId={logId}
        emailLogs={emailLogs}
      />
      <LayoutGrid>
        <LayoutGridItem xs={12}>
          <div style={{ height: `${gridHeight + 100}px`, overflow: 'auto !important' }}>
            <XGrid
              columns={columns}
              rows={documents || []}
              loading={promiseInProgress}
              pagination={false}
              hideFooter={true}
              toolbarProps={{
                leftActions: [
                  {
                    title: t('placement.profile.viewPackets'),
                    Icon: CreateNewFolderOutlinedIcon,
                    disabled: readOnly,
                    onClick: () => {
                      handleSubmitPacketDialogOpen();
                    },
                  },
                  ...CreateClientConfirmationXGridBtn({
                    disableDueToStatus: disableCreateDocBtn,
                    onClick: handleCreateCCBtnClick,
                    currentPlacementStatus: currentPlacementStatus,
                    statusChangeDecision: decision,
                    disabled: readOnly,
                  }),
                  ...CreateCandidateContractXGridBtn({
                    disableDueToStatus: disableCreateDocBtn,
                    onClick: () =>
                      redirectToCandidateContractDocEditor(history, location, { defaultSelection: 'draft' }),
                    currentPlacementStatus: currentPlacementStatus,
                    statusChangeDecision: decision,
                    disabled: readOnly || !contractBtnDecision?.enabled,
                  }),
                  ...DispatchArbitrationAgreementBtn({
                    currentPlacementStatus: currentPlacementStatus,
                    existingDocuments: documents,
                    onClick: () => setConfirmArbitrationAgreementCreation(true),
                    disabled: readOnly
                  }),
                  ...(wageRequestForm?.enabled
                    ? DispatchWageRequestBtn({
                        currentPlacementStatus: currentPlacementStatus,
                        existingDocuments: documents,
                        disabled: readOnly,
                        onClick: () =>
                          setConfirmationModal({ type: DocumentButtonType.Create_Wage_Request, open: true }),
                      })
                    : []),
                  ...(showCredentialingButton?.enabled
                    ? DispatchCredentialingButton({
                        onClick: () => {
                          const url = getCredentialingPacketGeneratorUrl(candidateId, placementId);
                          if (url.startsWith('/')) {
                            // For Train environment, use React Router navigation
                            history(url);
                          } else {
                            window.location.href = url;
                          }
                        },
                        disabled: readOnly,
                      })
                    : []),
                ],
                resetColumns: () => {
                  setColumns(deriveColumns(t, handleOnClick, setCredModalProps));
                },
                refreshButton: true,
                refreshGrid: () => {
                  dispatch(
                    placementDocumentsActions.getPlacementDocuments({
                      placementId: parseInt(params.placementId),
                    }),
                  );
                  if (wageRequestForm?.enabled) {
                    dispatch(
                      placementDetailsAction.wageRequestFormSignedStatus({
                        candidateId: candidateId,
                        placementId: parseInt(params.placementId),
                      }),
                    );
                  }
                },
              }}
              sortModel={sortModel}
              title={t('placement.profile.tabs.documentTab.title')}
              height={gridHeight}
              disableSelectionOnClick
              onSortModelChange={model => setSortModel(model)}
            />
          </div>
          <CredentialingPacketDialog
            open={credModalProps?.openCred}
            documentId={credModalProps?.documentId}
            handleClose={() =>
              setCredModalProps({
                openCred: false,
                documentId: '',
              })
            }
          />
          <ArbitrationAgreementConfirmationModal
            placementId={params?.placementId}
            open={confirmArbitrationAgreementCreation}
            onClose={() => {
              setConfirmArbitrationAgreementCreation(false);
            }}
          />
          <ConfirmationModal
            placementId={params?.placementId}
            confirmationModalDetails={confirmationModal}
            onClose={() => {
              setConfirmationModal({ type: '', open: false });
            }}
          />
        </LayoutGridItem>
      </LayoutGrid>
    </div>
  );
};
